import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import { formatDeltaPct, formatTry } from '../../utils/finance/financeFormat';
import { fx, fxKpiCardSx } from './financeTheme';

const ICONS = {
  ciro: TrendingUpOutlinedIcon,
  maliyet: Inventory2OutlinedIcon,
  brut: AccountBalanceWalletOutlinedIcon,
  gider: ReceiptLongOutlinedIcon,
  net: PaymentsOutlinedIcon,
  marj: PercentOutlinedIcon,
  sabit: ReceiptLongOutlinedIcon,
  degisken: ReceiptLongOutlinedIcon
};

export default function FinanceKpiCard({ label, value, subValue, delta, toneKey, hint, onDetail, testId, valueColor, iconKey }) {
  const accent = fx.kpi[toneKey] || fx.info;
  const deltaText = formatDeltaPct(delta);
  const Icon = ICONS[iconKey || toneKey] || TrendingUpOutlinedIcon;
  return (
    <Box
      data-testid={testId}
      onClick={onDetail}
      sx={{ ...fxKpiCardSx(accent), cursor: onDetail ? 'pointer' : 'default' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, mb: 0.55 }}>
        <Tooltip title={hint || ''} disableHoverListener={!hint}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: fx.muted, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        </Tooltip>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '7px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: `${accent}14`,
            color: accent,
            flexShrink: 0
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
      <Typography sx={{ fontSize: { xs: 17, md: 19, lg: 20 }, fontWeight: 800, color: valueColor || fx.text, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
      {subValue ? (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: fx.muted, lineHeight: 1.2, mt: 0.2, whiteSpace: 'nowrap' }}>
          {subValue}
        </Typography>
      ) : null}
      <Typography sx={{ mt: 0.4, fontSize: 12.5, fontWeight: 600, color: delta > 0 ? fx.success : delta < 0 ? fx.danger : fx.muted, minHeight: 16 }}>
        {deltaText || ' '}
      </Typography>
    </Box>
  );
}

/** Tutarin canli has SATIS fiyatina gore gram karsiligi: "694,03 gr HAS". */
export function formatHasGram(amountTry, hasSellTry) {
  if (amountTry == null || amountTry === '') return '';
  const amount = Number(amountTry);
  const rate = Number(hasSellTry);
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || rate <= 0) return '';
  const gram = Math.abs(amount) / rate;
  const digits = gram >= 100 ? 1 : 2;
  return `${gram.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} gr HAS`;
}

export function FinanceKpiGrid({ pnl, delta, onDetail, hasSellTry }) {
  const netLoss = (pnl?.netProfitTry || 0) < 0;
  const gram = (amountTry) => formatHasGram(amountTry, hasSellTry);
  const uncertain = (pnl?.costMissingSalesCount || 0) > 0;
  const mark = (value) => (uncertain ? `${value}*` : value);
  const items = [
    { key: 'ciro', label: 'Ciro', value: formatTry(pnl.revenueTry), subValue: gram(pnl.revenueTry), delta: delta?.revenueTry, toneKey: 'ciro', iconKey: 'ciro', detail: 'ciro', hint: 'Dönemdeki satışların toplamı.' },
    { key: 'maliyet', label: 'Maliyet', value: mark(formatTry(pnl.cogsTry)), subValue: gram(pnl.cogsTry), delta: delta?.cogsTry, toneKey: 'maliyet', iconKey: 'maliyet', detail: 'maliyet', hint: 'Maliyet kaydı tam olan satışların maliyeti.' },
    { key: 'brut', label: 'Brüt Kâr', value: mark(formatTry(pnl.grossProfitTry)), subValue: gram(pnl.grossProfitTry), delta: delta?.grossProfitTry, toneKey: 'brut', iconKey: 'brut', detail: 'kar', hint: 'Ciro − satılanların maliyeti (yalnız tam maliyetli satışlar).' },
    { key: 'gider', label: 'Gider', value: formatTry(pnl.expenseTry), subValue: gram(pnl.expenseTry), delta: delta?.expenseTry, toneKey: 'gider', iconKey: 'gider', detail: 'gider', hint: 'Aylık giderin bu döneme düşen payı.' },
    {
      key: 'net',
      label: netLoss ? 'Net Zarar' : 'Net Kâr',
      value: mark(formatTry(Math.abs(pnl.netProfitTry || 0))),
      subValue: gram(pnl.netProfitTry),
      delta: delta?.netProfitTry,
      toneKey: netLoss ? 'netDown' : 'netUp',
      iconKey: 'net',
      valueColor: netLoss ? fx.danger : fx.success,
      detail: 'net',
      hint: 'Brüt kâr − gider.'
    },
    {
      key: 'marj',
      label: 'Net Kâr Marjı',
      value: pnl.netMarginPct == null ? '—' : `%${Number(pnl.netMarginPct).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      delta: delta?.netMarginPct,
      toneKey: 'marj',
      iconKey: 'marj',
      hint: 'Satışlardan maliyet ve kayıtlı giderler çıktıktan sonra kalan tutarın ciroya oranı.'
    }
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
        gap: 1.15
      }}
    >
      {items.map((item) => (
        <FinanceKpiCard
          key={item.key}
          testId={`kpi-${item.key}`}
          label={item.label}
          value={item.value}
          subValue={item.subValue}
          delta={item.delta}
          toneKey={item.toneKey}
          iconKey={item.iconKey}
          valueColor={item.valueColor}
          hint={item.hint}
          onDetail={item.detail ? () => onDetail(item.detail) : undefined}
        />
      ))}
    </Box>
  );
}
